import { type FC } from "react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { useWorkflows } from "../context/WorkflowsContext";
import { packetContentsOf, partOn, type PacketPart } from "../packetContents";

type Props = {
  /** The configure popover: a labelled fieldset. The card omits the legend. */
  labelled?: boolean;
};

type RowProps = {
  part: PacketPart;
  labelled: boolean;
  on: boolean;
  onPick: (on: boolean) => void;
};

const PacketPartRow: FC<RowProps> = ({ part, labelled, on, onPick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: part.key });
  const id = `${labelled ? "cfg-" : ""}packet-part-${part.key}`;
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition
      }}
      className={cn(
        "flex items-center gap-2",
        isDragging && "z-10 bg-elevated"
      )}
      data-testid={id}
    >
      <button
        type="button"
        className="inline-flex shrink-0 cursor-grab touch-none text-fg-3 hover:text-fg-2 active:cursor-grabbing"
        aria-label={`Reorder ${part.title}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3.5" />
      </button>
      <Checkbox
        id={id}
        checked={on}
        onCheckedChange={(v) => onPick(v === true)}
      />
      <label htmlFor={id} className="min-w-0 flex-1 truncate text-sm text-fg">
        {part.title}
      </label>
      {!part.ready ? (
        <span className="shrink-0 text-sm text-fg-3">Not ready</span>
      ) : null}
    </div>
  );
};

/**
 * This case's work products, each checked for the next assemble. Firm
 * defaults seed the checks; a hand pick stays until the attorney changes it.
 * Drag a row to set the packet order.
 */
export const PacketContentsSelect: FC<Props> = ({ labelled = false }) => {
  const w = useWorkflows();
  const defaults = packetContentsOf(w.settings.packetSettings);
  const parts = w.facts.packetParts;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  if (!parts.length) return null;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const keys = parts.map((p) => p.key);
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    w.setPacketOrder(arrayMove(keys, from, to));
  };

  return (
    <fieldset className="flex flex-col gap-1" data-testid="packet-contents">
      {labelled ? (
        <legend className="mb-1 text-sm font-medium text-fg-2">
          Include In This Packet
        </legend>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={parts.map((p) => p.key)}
          strategy={verticalListSortingStrategy}
        >
          {parts.map((part) => (
            <PacketPartRow
              key={part.key}
              part={part}
              labelled={labelled}
              on={partOn(part, w.packetPicks, defaults)}
              onPick={(on) => w.setPacketPick(part.key, on)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </fieldset>
  );
};
